import { useCallback } from 'react';
import { getResponseBodyForAi } from '../../utils/responseBody';
import { aiMayReadResponses, RESPONSE_BODY_WITHHELD_NOTE } from '../../services/ai/responseAccess';
import { useTabsStore, setRequestTabSyncRevision, setRequestTabs } from '../../store/tabsStore';
import { useVariablesStore } from '../../store/variablesStore';
import { runScript } from '../../utils/scriptSandbox';
import { preRequestScriptBody } from '../../utils/scriptRequestMutations';
import { buildScriptVariableMap, withheldSecretKeys } from '../../utils/scriptVariables';
import { scriptEnvOverlaySnapshot } from '../../utils/scriptEnvOverlay';
import { secretVariableValues, redactSecretValues, summarizePreRequestDryRun } from '../../utils/aiPreRequestDryRun';
import {
  AI_RESPONSE_BODY_LIMIT,
  responseBodyString,
  redactTestRunResults,
  scriptScopesFromStore,
  tabFolderVariables,
  dryRunRequestUrl,
} from './aiRequestHelpers';

/** Assistant tools that read the last response and generate tests / pre-request scripts. */
export function useAiScriptGeneration() {
  // P1 — locate the request tab the AI is talking about: the one matching the
  // saved request_id, else the active tab.
  const findAiRequestTab = useCallback((requestId) => {
    const { requestTabs, activeRequestTabId } = useTabsStore.getState();
    if (requestId != null) {
      const byId = requestTabs.find((t) => String(t.originalId) === String(requestId));
      if (byId) return byId;
    }
    return requestTabs.find((t) => t.id === activeRequestTabId) || null;
  }, []);

  // P1 — return the last captured response for a request (the one place the model
  // can read a body; send_request only surfaces status). Body is truncated.
  const aiGetLastResponse = useCallback(async (input = {}) => {
    const tab = findAiRequestTab(input.request_id);
    if (!tab) return { error: 'No active request tab' };
    const response = tab.response;
    if (!response) {
      return { error: 'No response captured yet. Send the request first, then ask again.' };
    }
    // The privacy gate (owner decision 13.09.2026): no body unless the user
    // opted in, and a body that does cross is redacted first.
    const gated = getResponseBodyForAi(
      { ...response, data: responseBodyString(response) },
      { allowed: aiMayReadResponses(), maxChars: AI_RESPONSE_BODY_LIMIT }
    );
    const headers = response.headers || {};
    if (gated.blocked) {
      return {
        request_id: tab.originalId ?? null,
        status: response.status,
        status_text: response.statusText ?? response.status_text ?? '',
        time_ms: response.time ?? null,
        content_type: headers['content-type'] || headers['Content-Type'] || null,
        body_withheld: true,
        note: RESPONSE_BODY_WITHHELD_NOTE,
      };
    }
    return {
      request_id: tab.originalId ?? null,
      status: response.status,
      status_text: response.statusText ?? response.status_text ?? '',
      time_ms: response.time ?? null,
      content_type: gated.content_type,
      body: gated.body,
      body_truncated: gated.body_truncated,
      redaction_count: gated.redaction_count,
    };
  }, [findAiRequestTab]);

  // P1 — write a model-authored test script onto the request tab (marked unsaved so
  // the user reviews/saves) and run it against the STORED response (no re-send),
  // returning pass/fail plus the previous script for a diff.
  const aiGenerateTests = useCallback(async (input = {}) => {
    const testScript = typeof input.test_script === 'string' ? input.test_script : '';
    if (!testScript.trim()) return { error: 'test_script is required' };

    const tab = findAiRequestTab(input.request_id);
    if (!tab) return { error: 'No active request tab' };

    const previous = tab.testScript ?? '';
    setRequestTabs((prev) =>
      prev.map((t) => (t.id === tab.id ? { ...t, testScript, hasUnsavedChanges: true } : t))
    );
    // Reseed the RequestBuilder editor so the new script shows immediately.
    setRequestTabSyncRevision((n) => n + 1);

    let tests = null;
    let ranError = null;
    const scopes = scriptScopesFromStore(useVariablesStore.getState(), tab.collectionId, tabFolderVariables(tab));
    // Includes granted secrets — see redactTestRunResults. It is a backstop for
    // the host-side throw below and for a script that echoes a granted value
    // into an assertion message, not a containment boundary.
    const secretValues = secretVariableValues(scopes);
    // Response-privacy gate (13.09.2026, review R1). Running the model's script
    // against the stored body and reporting ANY outcome — names, errors, or even
    // just pass/fail per test — is a read of that body: a script can branch on
    // one bit of it per test. With "Let AI read responses" off the script is
    // written to the Tests tab for the user and NOT run; the user runs it with
    // Send.
    const mayRun = aiMayReadResponses();
    if (tab.response && mayRun) {
      try {
        // The SEND path's secret policy, deliberately — NOT the pre-request
        // tool's `allSecretsWithheld`. `generate_tests` is not a dry run: it
        // executes the model's tests against a stored response and reports real
        // pass/fail, which the user then acts on. Withholding a
        // `scriptAccess: true` secret here would make a legitimate test — one
        // that checks a signature or an authenticated field — fail for a reason
        // that has nothing to do with the API, and the model would then "repair"
        // a correct test into a wrong one. So this matches what Send would
        // actually have given the same script (RequestBuilder.js's test-script
        // call site): withhold `secret && !scriptAccess`, honour the grant.
        //
        // What that closes is the real hole: before this, the map came from a
        // flatten that ignored `secret` entirely AND no `withheldKeys` were
        // passed, so EVERY secret in EVERY scope was readable by a
        // model-authored script with no grant of any kind — and an ordinary
        // failing assertion prints the value it read into `tests[].error`,
        // which goes to the model verbatim.
        //
        // Of the two lines, `withheldKeys` is the load-bearing one:
        // `buildSandboxPayload` deletes every named key from whatever map it is
        // handed, so naming them is on its own enough to keep the value out of
        // the worker (measured — see the ablation note in
        // aiGenerateTestsSecrets.test.js). `buildScriptVariableMap` is here for
        // the second reason: it folds in `scriptEnvOverlaySnapshot()`, so a
        // token an earlier script set this session is what these tests read,
        // exactly as at Send. The old flatten had no overlay, so the AI run
        // could report a failure Send would never produce.
        const outcome = await runScript(testScript, {
          environmentVariables: buildScriptVariableMap(scopes, scriptEnvOverlaySnapshot()),
          withheldKeys: withheldSecretKeys(scopes),
          request: { url: tab.url, method: tab.method, headers: {} },
          response: {
            status: tab.response.status,
            data: tab.response.data,
            headers: tab.response.headers || {},
            time: tab.response.time ?? null,
          },
        });
        tests = outcome.errors?.length
          ? [{ name: 'Script error', passed: false, error: outcome.errors[0] }]
          : outcome.tests || [];
      } catch (err) {
        // String(), not `err.message` raw. `redactSecretValues` returns any
        // non-string input UNCHANGED, so an Error whose `.message` is an object
        // would carry a granted secret straight past the scrubber and into
        // `run_error`, which condenseToolResultForApi forwards verbatim.
        // `redactTestRunResults` already coerces on its path; this is the same
        // rule applied to the one field that did not.
        ranError = String(err?.message || err || '');
      }
    }

    // Scrubbed BEFORE the counts, so the counts are taken off the same array the
    // model is shown.
    const redactedTests = redactTestRunResults(tests, secretValues);
    const passed_count = redactedTests.filter((t) => t.passed).length;
    const failed_count = redactedTests.filter((t) => !t.passed).length;
    return {
      success: true,
      request_id: tab.originalId ?? null,
      had_previous_tests: Boolean(previous.trim()),
      // Client-side only: `condenseToolResultForApi` does not forward either
      // script. The previous one is the user's, and the new one the model just
      // wrote. Neither is scrubbed, because scrubbing them would corrupt the
      // text the editor and the diff show.
      previous_test_script: previous,
      test_script: testScript,
      ran_against_stored_response: Boolean(tab.response) && mayRun && !ranError,
      run_withheld: Boolean(tab.response) && !mayRun,
      // A host-side throw, not a script one — but it can carry a value folded
      // into a message, so it gets the same backstop.
      run_error: ranError === null ? null : redactSecretValues(ranError, secretValues),
      passed_count,
      failed_count,
      tests: redactedTests,
    };
  }, [findAiRequestTab]);

  // P1 — write a model-authored PRE-REQUEST script onto the request tab (marked
  // unsaved so the user reviews/saves) and DRY-RUN it.
  //
  // Why this is not a `target` flag on aiGenerateTests: a pre-request script
  // runs before anything is sent, so there is no response to run it against and
  // no pass/fail to report. The validation that IS available is "does it
  // compile, does it throw, what would it set", which is a different result
  // shape and a different pm.* surface (no pm.response).
  //
  // The dry run is deliberately side-effect free. RequestBuilder's send path
  // ends with `applyScriptEnvMutations(preResult.envMutations)`; this one must
  // NOT, and there is no equivalent call below. The user has not reviewed or
  // saved this script yet — silently repointing {{baseUrl}} for every other tab
  // while "generating" a script would be indistinguishable from the COL-03 bug
  // the overlay was built to fix. Only Send applies a pre-request script's
  // writes. If you add an apply call here, `aiPreRequestScriptTool.test.js`
  // fails, which is the point.
  const aiGeneratePreRequestScript = useCallback(async (input = {}) => {
    const preRequestScript =
      typeof input.pre_request_script === 'string' ? input.pre_request_script : '';
    if (!preRequestScript.trim()) return { error: 'pre_request_script is required' };

    const tab = findAiRequestTab(input.request_id);
    if (!tab) return { error: 'No active request tab' };

    const previous = tab.preRequestScript ?? '';
    setRequestTabs((prev) =>
      prev.map((t) =>
        (t.id === tab.id
          // editorTab is set because the reseed below re-applies it: without it
          // `normalizeEditorTab(undefined)` lands the user on Params, i.e. the
          // AI writes a script and the editor visibly jumps somewhere else.
          ? { ...t, preRequestScript, editorTab: 'script', hasUnsavedChanges: true }
          : t)
      )
    );
    // Load-bearing: RequestBuilder's sync effect only diffs url/method/headers/
    // body, so a preRequestScript change reaches the editor ONLY via a revision
    // bump. Without this the script sits in the store and the Pre-request tab
    // keeps showing the old text — exactly the "AI isn't putting it in" symptom.
    setRequestTabSyncRevision((n) => n + 1);

    let dryRun;
    try {
      const vars = useVariablesStore.getState();
      const scopes = scriptScopesFromStore(vars, tab.collectionId, tabFolderVariables(tab));
      // EVERY secret is withheld here, `scriptAccess: true` grants included —
      // the one place in the app where that is true. The grant means "the Send
      // path may use this value"; it does not mean an AI-authored script may
      // read it and ship whatever it derives to the vendor. A dry run only has
      // to answer "does this compile, does it throw, what would it set", which
      // needs no real secret. With no secret in the sandbox there is no value
      // to encode into a variable NAME, a pm.test label or an error string, so
      // the whole covert-channel class is gone rather than filtered. The cost —
      // a script that hashes a granted secret hits the withheld sentinel and
      // reports an error the real Send would not — is documented in the tool
      // description so the model does not chase it.
      const dryRunSecrecy = { allSecretsWithheld: true };
      const variableMap = buildScriptVariableMap(
        scopes,
        scriptEnvOverlaySnapshot(),
        dryRunSecrecy
      );
      const outcome = await runScript(preRequestScript, {
        environmentVariables: variableMap,
        withheldKeys: withheldSecretKeys(scopes, dryRunSecrecy),
        request: {
          // F-1: the raw `{{baseUrl}}/v1/login` made `new URL(pm.request.url)`
          // throw in the dry run and succeed at Send, handing the model a hard
          // error on working code. Substituted, like RequestBuilder's
          // buildUrl(). Secrets stay literal because `variableMap` omits them —
          // a URL is inside the sandbox, so substituting one in would reopen
          // exactly the channel the line above closes.
          url: dryRunRequestUrl(tab, variableMap),
          method: tab.method,
          // F-2: NOT the tab's headers. The Send path passes no headers at all
          // (RequestBuilder.js), so `pm.request.headers.get('Authorization')`
          // is null there. Populating it here certified scripts that are
          // silently broken in production, which is worse than not validating.
          headers: {},
          // E-1: pm.info.requestName/requestId are documented; without these
          // they are permanently ''.
          name: tab.name || '',
          id: tab.originalId ?? tab.id ?? '',
          // Bruno's req.getBody(): the body as typed, like Send and the runner
          // (utils/scriptRequestMutations). Without it getBody() refuses here.
          body: preRequestScriptBody(tab),
        },
        // No response exists before a request. This is the whole reason the
        // tool is separate from generate_tests.
        response: null,
      });
      dryRun = summarizePreRequestDryRun(outcome, {
        secretValues: secretVariableValues(scopes),
      });
    } catch (err) {
      dryRun = {
        ok: false,
        error: redactSecretValues(
          err?.message || String(err),
          secretVariableValues(scriptScopesFromStore(useVariablesStore.getState(), tab.collectionId, tabFolderVariables(tab)))
        ),
        failed_tests: [],
        would_set_environment_variables: [],
        would_unset_environment_variables: [],
        would_set_local_variables: [],
      };
    }

    return {
      success: true,
      request_id: tab.originalId ?? null,
      had_previous_script: Boolean(previous.trim()),
      previous_pre_request_script: previous,
      pre_request_script: preRequestScript,
      dry_run: dryRun,
      // Stated explicitly so neither the model nor a future reader assumes the
      // dry run touched anything.
      applied_to_environment: false,
    };
  }, [findAiRequestTab]);

  return { aiGetLastResponse, aiGenerateTests, aiGeneratePreRequestScript };
}
